import { memo } from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import { Stars, RemoveCircle, Error } from '@mui/icons-material';
import ProgressBar from '@/components/common/ProgressBar';
import { getQualityScoreColor, getQualityScoreDescription, getQualityScoreLevel } from '@/utils/calls';
import { QUALITY_SCORE_LEVELS } from '@/constants';

interface QualityScoreInfoItemProps {
    label: string;
    labelId: string;
    score: number;
}

/**
 * Displays the call quality score with color-coded card, progress bar and status.
 * Score is expected to be a percentage value between 0-100.
 *
 * @param {QualityScoreInfoItemProps} props
 * @returns {JSX.Element}
 */
const STATUS_ICONS = {
    [QUALITY_SCORE_LEVELS.GOOD]: Stars,
    [QUALITY_SCORE_LEVELS.NEUTRAL]: RemoveCircle,
    [QUALITY_SCORE_LEVELS.NEEDS_ATTENTION]: Error,
} as const;

const QualityScoreInfoItem: React.FC<QualityScoreInfoItemProps> = ({ label, labelId, score }) => {
    const theme = useTheme();

    const roundedScore = Math.round(score);
    const qualityLevel = getQualityScoreLevel(score);
    const scoreColor = getQualityScoreColor(score, 'dark');
    const backgroundColor = getQualityScoreColor(score, 'light');
    const tooltipText = getQualityScoreDescription(score);
    const StatusIcon = STATUS_ICONS[qualityLevel];
    const trackColor = theme.palette.white[100];

    return (
        <Box
            data-testid="quality-score"
            aria-labelledby={labelId}
            sx={{
                width: '100%',
                borderRadius: 1.5,
                backgroundColor: backgroundColor,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography id={labelId} fontWeight={500}>
                    {label}
                </Typography>
                <Tooltip title={tooltipText} arrow placement="top">
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <Typography variant="h4" fontWeight={600}>
                            {roundedScore}%
                        </Typography>
                        <StatusIcon sx={{ fontSize: '1rem', color: scoreColor }} />
                    </Box>
                </Tooltip>
            </Box>
            <ProgressBar value={roundedScore} barColor={scoreColor} trackColor={trackColor} />
        </Box>
    );
};

export default memo(QualityScoreInfoItem);
